import { useCallback, useState } from 'react';
import { Alert, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';
import {
  ArrowLeft,
  Bell,
  ChevronRight,
  CircleHelp,
  Database,
  LogOut,
  ShieldCheck,
  UserRound,
} from 'lucide-react-native';
import { AppButton } from '../components/ui/AppButton';
import { AppCard } from '../components/ui/AppCard';
import { AppText } from '../components/ui/AppText';
import { showAppToast } from '../components/ui/AppToast';
import { tokens } from '../theme/tokens';
import { withdrawPilotConsentAndLogout } from '../utils/pilotTelemetry';

type SettingsRow = {
  id: string;
  title: string;
  description: string;
  Icon: typeof UserRound;
  tone?: 'danger';
};

const settingsRows: SettingsRow[] = [
  {
    id: 'account',
    title: '账号管理',
    description: '手机号、学生信息和年级档案',
    Icon: UserRound,
  },
  {
    id: 'notifications',
    title: '消息通知',
    description: '学习提醒、计划提醒和错题复习',
    Icon: Bell,
  },
  {
    id: 'privacy',
    title: '协议与隐私',
    description: '用户协议、隐私政策和授权说明',
    Icon: ShieldCheck,
  },
  {
    id: 'clear-data',
    title: '清除学习数据',
    description: '待产品确认清除范围和二次确认弹窗',
    Icon: Database,
    tone: 'danger',
  },
  {
    id: 'about',
    title: '关于登峰',
    description: '版本信息、客服与反馈入口',
    Icon: CircleHelp,
  },
];

export default function SettingsPage() {
  const router = useRouter();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const showPendingToast = useCallback((title: string) => {
    showAppToast(`${title}待设计确认`, 'info');
  }, []);

  const logout = useCallback(async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      await withdrawPilotConsentAndLogout();
      router.replace('/login');
    } catch {
      showAppToast('本地会话清理失败，请重试', 'error');
    } finally {
      setIsLoggingOut(false);
    }
  }, [isLoggingOut, router]);

  const confirmLogout = useCallback(() => {
    Alert.alert('退出登录', '将清除本机登录状态与尚未发送的匿名行为队列。', [
      { text: '取消', style: 'cancel' },
      { text: '退出', style: 'destructive', onPress: () => void logout() },
    ]);
  }, [logout]);

  return (
    <SafeAreaView style={styles.screen}>
      <View style={styles.header}>
        <AppButton
          label="我的"
          variant="ghost"
          onPress={() => router.back()}
          leftSlot={<ArrowLeft size={18} color={tokens.colors.primary} />}
          style={styles.backButton}
        />
        <View style={styles.headerCopy}>
          <AppText variant="title" weight="bold">
            设置
          </AppText>
          <AppText variant="caption" tone="muted">
            账号、偏好与关于
          </AppText>
        </View>
      </View>

      <ScrollView contentContainerStyle={styles.content}>
        <AppCard style={styles.noticeCard}>
          <AppText variant="subtitle" weight="bold">
            设置页待设计确认
          </AppText>
          <AppText variant="body" tone="muted">
            Figma 里目前只有“我的页面”的设置入口，没有独立设置页。本页先提供一期可点击骨架，后续按设计稿替换为正式页面。
          </AppText>
        </AppCard>

        <View style={styles.rowGroup}>
          {settingsRows.map(({ id, title, description, Icon, tone }) => (
            <Pressable
              key={id}
              accessibilityRole="button"
              onPress={() => showPendingToast(title)}
              style={({ pressed }) => [pressed ? styles.pressed : null]}
            >
              <AppCard style={styles.rowCard}>
                <View style={[styles.iconWrap, tone === 'danger' ? styles.dangerIcon : null]}>
                  <Icon
                    size={20}
                    color={tone === 'danger' ? tokens.colors.danger : tokens.colors.primary}
                  />
                </View>
                <View style={styles.rowCopy}>
                  <AppText
                    variant="body"
                    weight="bold"
                    tone={tone === 'danger' ? 'danger' : 'default'}
                  >
                    {title}
                  </AppText>
                  <AppText variant="caption" tone="muted">
                    {description}
                  </AppText>
                </View>
                <ChevronRight size={18} color={tokens.colors.textSubtle} />
              </AppCard>
            </Pressable>
          ))}
        </View>

        <AppButton
          label="退出登录"
          variant="secondary"
          onPress={confirmLogout}
          loading={isLoggingOut}
          leftSlot={<LogOut size={18} color={tokens.colors.primary} />}
        />
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: tokens.colors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.md,
    paddingHorizontal: tokens.layout.screenPadding,
    paddingTop: tokens.spacing.md,
    paddingBottom: tokens.spacing.sm,
  },
  backButton: {
    minHeight: 40,
    paddingHorizontal: tokens.spacing.sm,
  },
  headerCopy: {
    flex: 1,
  },
  content: {
    paddingHorizontal: tokens.layout.screenPadding,
    paddingTop: tokens.spacing.md,
    paddingBottom: tokens.spacing.xxl,
    gap: tokens.spacing.lg,
  },
  noticeCard: {
    gap: tokens.spacing.sm,
    backgroundColor: tokens.colors.surfaceMuted,
  },
  rowGroup: {
    gap: tokens.spacing.md,
  },
  rowCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.md,
  },
  iconWrap: {
    width: 40,
    height: 40,
    borderRadius: tokens.radius.full,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colors.surfaceMuted,
  },
  dangerIcon: {
    backgroundColor: tokens.colors.dangerSoft,
  },
  rowCopy: {
    flex: 1,
    gap: tokens.spacing.xs,
  },
  pressed: {
    opacity: 0.86,
  },
});
